/**
 * Shared design tokens for the Tulu Printers dashboard (PrinterDb tree).
 * Palette matches Tulu's real brand colors as defined in the app
 * (tulu_app/lib/Common/color_select.dart's ColorSelect class) — lavender
 * indigo / electric indigo purple system, not an arbitrary mockup choice.
 */
export const theme = {
  primary: "#9747FF", // lavenderIndigo
  primaryHover: "#740AFF", // electricIndigo
  primaryLight: "#F8F1FF", // lavenderBlush

  accent: "#740AFF", // electricIndigo
  accentHover: "#5A08C7",
  accentLight: "#E5CFFB", // paleLavender

  background: "#FAFAFA", // backgroundFriend
  surface: "#FFFFFF",

  border: "#EDEDED",
  borderStrong: "#d9d9d9", // disableGray

  text: "#000000", // darkCharcoal
  textMuted: "#4A4A4A", // quartz
  textFaint: "#868992", // argent

  success: "#2C6B3F",
  successLight: "#EAF7EE",
  warning: "#8A5A08",
  warningLight: "#FFF6E4",
  danger: "#FF4D6D", // infraRed
  dangerLight: "#FFE2E7",

  info: "#457B9D", // jellyBeanBlue
  infoLight: "#E7F0F5",

  radius: "18px",
  radiusSm: "12px",
  radiusPill: "999px",

  shadow: "0 1px 2px rgba(28, 28, 97, 0.04)",
  shadowHover: "0 10px 28px rgba(28, 28, 97, 0.10)",

  fontFamily:
    "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
};

/** Status → pill colors, shared by every job-list layout (table/grouped/timeline). */
export const statusMeta = {
  Completed: { label: "Completed", color: theme.success, background: theme.successLight, dot: theme.success },
  printing: { label: "Printing", color: theme.primaryHover, background: theme.primaryLight, dot: theme.primary },
  wait: { label: "Queued", color: theme.warning, background: theme.warningLight, dot: theme.warning },
  failed: { label: "Failed", color: theme.danger, background: theme.dangerLight, dot: theme.danger },
  Cancel: { label: "Cancelled", color: theme.textFaint, background: theme.background, dot: theme.textFaint },
  // PrintJobStatusRpi.OLD — a historical job whose outcome the printer never
  // reported (terminal, never refundable). Without its own entry it fell through
  // getStatusMeta's fallback and showed as "Queued", which made months-old jobs
  // look stuck in the queue once the per-printer history view existed.
  old: { label: "Not reported", color: theme.textMuted, background: theme.border, dot: theme.textMuted },
};

export const getStatusMeta = (status) => statusMeta[status] || statusMeta.wait;

// Shared by the sidebar's "Print jobs" badge (left_panel.js) and the jobs-list
// filter chips (job_list_header.js/printer_jobs.js) so both always agree on what
// counts as "active" — queued and printing are grouped into a single bucket.
export const ACTIVE_JOB_STATUSES = ["wait", "printing"];

export const STATUS_FILTER_GROUPS = [
  { key: "queued", label: "Queued + Printing", statuses: ACTIVE_JOB_STATUSES },
  { key: "Completed", label: "Completed", statuses: ["Completed"] },
  { key: "failed", label: "Failed", statuses: ["failed"] },
  { key: "Cancel", label: "Cancelled", statuses: ["Cancel"] },
  { key: "old", label: "Not reported", statuses: ["old"] },
];
